/* eslint-disable @typescript-eslint/naming-convention */
import * as Sentry from '@sentry/browser';
import {Dayjs} from 'dayjs';
import {Effect} from 'effect';
import {InterruptedException} from 'effect/Cause';

import {ConcurrentActivityIdsLoader} from 'services/Effect/ActivityIdsLoader';
import {EffectError} from 'services/Effect/errors';
import {runLoaderEffect} from 'services/Effect/helpers';
import {ConcurrentIssuesLoader} from 'services/Effect/IssuesLoader';
import {ConcurrentProjectLoader} from 'services/Effect/ProjectLoader';
import {ConcurrentTaskLoader} from 'services/Effect/TasksLoader';
import {TaskFilterQuery, TaskStatusQuery} from 'shared/models/task/filter';
import {IssueModelRawDTO, TaskDetailsModelDTO, TaskGanttModel, TaskModelRawDTO} from 'shared/models/task/task';
import {TaskDependencyDto} from 'shared/models/TaskDependency';

import {LoadOptions} from '../types';

export type LoaderOptions = {
  name: string;
  projectId: string;
  extra?: Record<string, unknown>;
};

export async function loadActivityIds(options: LoadOptions) {
  const {setLoading, done, shouldLoadOperations} = options;

  setLoading?.(true);

  const program = Effect.gen(function* () {
    try {
      const loader = yield* ConcurrentActivityIdsLoader;

      // handle each call separately because either can succeed
      const activityIds = yield* loader.loadActivityIds(options).pipe(
        Effect.catchAll((error) => {
          if (error instanceof InterruptedException) {
            // we succeed with an empty array if the activityIds calls are interrupted
            // this guarantees we will not error when [] is undefined
            return Effect.succeed([]);
          }
          Sentry.captureException(error, {
            tags: {type: 'loader.loadActivityIds'},
          });
          return Effect.succeed([]);
        }),
      );

      if (shouldLoadOperations) {
        const operationIds = yield* loader.loadOperationIds(options).pipe(
          Effect.catchAll((error) => {
            if (error instanceof InterruptedException) {
              // we succeed with an empty array if the operationIds calls are interrupted
              // this guarantees we will not error when [] is undefined
              return Effect.succeed([]);
            }
            Sentry.captureException(error, {
              tags: {type: 'loader.loadOperationIds'},
            });
            return Effect.succeed([]);
          }),
        );

        const combinedIds = activityIds.concat(operationIds);
        done?.(combinedIds);
        return void 0;
      }
      done?.(activityIds);
      return void 0;
    } catch (err) {
      throw err;
    } finally {
      setLoading?.(false);
    }
  });

  return runLoaderEffect<void | InterruptedException, EffectError, ConcurrentActivityIdsLoader>(
    program,
    ConcurrentActivityIdsLoader.Default,
    {
      name: 'loadActivityIds',
      projectId: options.projectId,
      extra: {
        queryParams: options.queryParams,
        dataRange: options.dataRange,
      },
    },
  );
}

export async function loadAllTasks(projectId: string, loadDeleted: boolean) {
  const program = Effect.gen(function* () {
    const taskLoader = yield* ConcurrentTaskLoader;

    // handle each call separately because either can succeed
    const activities = yield* taskLoader.loadAllTasks(projectId, loadDeleted).pipe(
      Effect.catchAll((error) => {
        if (error instanceof InterruptedException) {
          // we succeed with an empty array if the all tasks calls are interrupted
          // this guarantees we will not error when [] is undefined
          return Effect.succeed([]);
        }
        Sentry.captureException(error, {
          tags: {type: 'taskLoader.loadAllTasks'},
        });
        return Effect.succeed([]);
      }),
    );

    return activities;
  });

  return runLoaderEffect<TaskModelRawDTO[], EffectError, ConcurrentTaskLoader>(program, ConcurrentTaskLoader.Default, {
    name: 'loadAllTasks',
    projectId,
  });
}

export async function loadAllOperations(projectId: string) {
  const program = Effect.gen(function* () {
    const taskLoader = yield* ConcurrentTaskLoader;

    const operations = yield* taskLoader.loadOperations(projectId).pipe(
      Effect.catchAll((error) => {
        if (error instanceof InterruptedException) {
          // we succeed with an empty array if the all operations calls are interrupted
          // this guarantees we will not error when [] is undefined
          return Effect.succeed([]);
        }
        Sentry.captureException(error, {
          tags: {type: 'taskLoader.loadOperations'},
        });
        return Effect.succeed([]);
      }),
    );

    return operations;
  });

  return runLoaderEffect<TaskModelRawDTO[], EffectError, ConcurrentTaskLoader>(program, ConcurrentTaskLoader.Default, {
    name: 'loadAllOperations',
    projectId,
  });
}

export async function loadIssues(options: LoadOptions) {
  const program = Effect.gen(function* () {
    const issuesLoader = yield* ConcurrentIssuesLoader;
    const result = yield* issuesLoader.loadIssues(options);
    return result;
  }).pipe(Effect.map((result) => result as IssueModelRawDTO[]));

  return runLoaderEffect<IssueModelRawDTO[], EffectError, ConcurrentIssuesLoader>(
    program,
    ConcurrentIssuesLoader.Default,
    {
      name: 'loadIssues',
      projectId: options.projectId,
      extra: {
        queryParams: options.queryParams,
        dataRange: options.dataRange,
      },
    },
  );
}

export async function fetchGanttTasksByDate(projectId: string, begin: Dayjs, end: Dayjs) {
  const program = Effect.gen(function* () {
    const taskLoader = yield* ConcurrentTaskLoader;
    return yield* taskLoader.fetchTasksByDate(projectId, begin.toDate(), end.toDate());
  });

  return runLoaderEffect<TaskGanttModel[], EffectError, ConcurrentTaskLoader>(program, ConcurrentTaskLoader.Default, {
    name: 'fetchTasksByDate',
    projectId,
  });
}

export async function fetchGanttTasksDetailsByDate(params: Partial<TaskFilterQuery & TaskStatusQuery>) {
  const program = Effect.gen(function* () {
    const taskLoader = yield* ConcurrentTaskLoader;
    return yield* taskLoader.loadTasksByDateIntersect(params);
  });

  return runLoaderEffect<TaskDetailsModelDTO[], EffectError, ConcurrentTaskLoader>(
    program,
    ConcurrentTaskLoader.Default,
    {
      name: 'loadTasksByDateIntersect',
      projectId: params.projectId,
    },
  );
}

export async function fetchBlockedTasks(projectId: string) {
  const program = Effect.gen(function* () {
    const taskLoader = yield* ConcurrentTaskLoader;
    return yield* taskLoader.fetchBlockedTasks(projectId);
  });

  return runLoaderEffect<TaskGanttModel[], EffectError, ConcurrentTaskLoader>(program, ConcurrentTaskLoader.Default, {
    name: 'fetchBlockedTasks',
    projectId,
  });
}

export async function loadDailiesIssues(projectId: string, queryParams: Partial<TaskFilterQuery>) {
  const program = Effect.gen(function* () {
    const issuesLoader = yield* ConcurrentIssuesLoader;
    return yield* issuesLoader.loadDailiesIssues({
      projectId,
      queryParams,
    });
  }).pipe(Effect.map((result) => result as IssueModelRawDTO[]));

  return runLoaderEffect<IssueModelRawDTO[], EffectError, ConcurrentIssuesLoader>(
    program,
    ConcurrentIssuesLoader.Default,
    {
      name: 'loadDailiesIssues',
      projectId,
      extra: {
        queryParams,
      },
    },
  );
}

export async function loadProjectDependencies(projectId: string, taskIds: string[] = []) {
  const program = Effect.gen(function* () {
    const dependenciesLoader = yield* ConcurrentProjectLoader;
    const filteredTaskIds = taskIds.filter((tId) => !tId.startsWith('placeholder'));
    const result = yield* dependenciesLoader.loadDependencies({
      projectId,
      taskIds: filteredTaskIds,
    });

    if (result instanceof InterruptedException) {
      return [] as TaskDependencyDto[];
    }

    return result ?? [];
  }).pipe(Effect.map((result) => result));

  return runLoaderEffect<TaskDependencyDto[], EffectError, ConcurrentProjectLoader>(
    program,
    ConcurrentProjectLoader.Default,
    {
      name: 'loadProjectDependancies',
      projectId,
    },
  );
}
